import { useCallback, useRef, useState, type ReactElement } from 'react'
import { MenuItem } from '@mui/material'
import {
  Close as CloseIcon,
  Send as SendIcon,
  VpnKey as VpnKeyIcon,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { invitationsApi } from '@/api/invitations'
import {
  CabinetInvitationDialog,
  type CabinetInvitationDialogView,
} from '@/components/users/CabinetInvitationDialog'
import type { Invitation, User } from '@/types'
import { canManageCabinet } from '@/utils/cabinetInvitation'
import { displayErrorMessage, getErrorMessage, getValidationErrors } from '@/utils/error'
import { getUserDisplayName, type UserNameSource } from '@/utils/userName'

/** Ученик, с чьим кабинетом работаем */
export interface CabinetInvitationStudent {
  id: number
  name: string
}

/** Сообщение для Alert на странице: итог отмены приглашения */
export interface CabinetActionMessage {
  type: 'success' | 'error'
  text: string
}

interface UseCabinetInvitationActionsOptions {
  /**
   * Приглашение создано или отправлено заново (`invitation`) либо отменено (null).
   * Вызывается и тогда, когда диалог закрыли, не дождавшись ответа, — изменение
   * на сервере уже случилось.
   */
  onChanged: (student: CabinetInvitationStudent, invitation: Invitation | null) => void
  onMessage: (message: CabinetActionMessage) => void
}

type CabinetUser = Pick<User, 'id' | 'has_cabinet' | 'contract' | 'cabinet_invitation'> & UserNameSource

/**
 * Выдача кабинета ученику без кабинета: пункты меню «Создать кабинет»,
 * «Отправить приглашение ещё раз», «Отменить приглашение» и диалог с формой
 * и ссылкой. Общий для списка учеников, карточки в мобильном списке, карточки
 * ученика и вкладки приглашений.
 */
export function useCabinetInvitationActions({ onChanged, onMessage }: UseCabinetInvitationActionsOptions) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [view, setView] = useState<CabinetInvitationDialogView | null>(null)
  const [student, setStudent] = useState<CabinetInvitationStudent | null>(null)
  // Номер открытия диалога: ответ на запрос из прошлого открытия диалог не трогает
  const sessionRef = useRef(0)

  const startSession = (next: CabinetInvitationStudent, nextView: CabinetInvitationDialogView): number => {
    sessionRef.current += 1
    setStudent(next)
    setView(nextView)
    setOpen(true)
    return sessionRef.current
  }

  /** Обновить содержимое, только если диалог открыт тем же действием */
  const updateView = (session: number, update: CabinetInvitationDialogView) => {
    if (session === sessionRef.current) {
      setView(update)
    }
  }

  const openCreate = (target: CabinetInvitationStudent) => {
    startSession(target, {
      mode: 'create',
      studentName: target.name,
      submitting: false,
      error: null,
      emailError: null,
    })
  }

  const openResend = async (target: CabinetInvitationStudent, invitationId: number) => {
    // Модалка открывается сразу, ответ API — скелетоном внутри
    const session = startSession(target, { mode: 'resend', studentName: target.name, error: null })
    try {
      const invitation = await invitationsApi.resendInvitation(invitationId)
      onChanged(target, invitation)
      updateView(session, {
        mode: 'result',
        studentName: target.name,
        invitation,
        // API шлёт письмо при каждой повторной отправке приглашения в кабинет
        emailSent: true,
      })
    } catch (err) {
      // 403 — договора с учеником больше нет (InvitationPolicy::resend). Отказ
      // политики API присылает непереведённым «This action is unauthorized.»
      const forbidden = (err as { response?: { status?: number } } | null)?.response?.status === 403
      updateView(session, {
        mode: 'resend',
        studentName: target.name,
        error: forbidden
          ? t('users.cabinetInvitation.resendForbidden')
          : displayErrorMessage(getErrorMessage(err), t('users.cabinetInvitation.resendError')),
      })
    }
  }

  const submitCreate = async ({ email, sendEmail }: { email: string; sendEmail: boolean }) => {
    if (student === null || view?.mode !== 'create') return
    const session = sessionRef.current
    const target = student
    setView({ ...view, submitting: true, error: null, emailError: null })
    try {
      const invitation = await invitationsApi.createCabinetInvitation(target.id, {
        email,
        send_email: sendEmail,
      })
      onChanged(target, invitation)
      updateView(session, { mode: 'result', studentName: target.name, invitation, emailSent: sendEmail })
    } catch (err) {
      // Отказ по почте (занята, неверный формат) — под полем; остальное — над формой
      const emailError = getValidationErrors(err)?.email?.[0] ?? null
      updateView(session, {
        mode: 'create',
        studentName: target.name,
        submitting: false,
        emailError,
        error: emailError
          ? null
          : displayErrorMessage(getErrorMessage(err), t('users.cabinetInvitation.createError')),
      })
    }
  }

  const cancelInvitation = async (target: CabinetInvitationStudent, invitationId: number, email: string | null) => {
    if (!window.confirm(t('users.cabinetInvitation.cancelConfirm', { email: email ?? '—' }))) {
      return
    }
    try {
      await invitationsApi.deleteInvitation(invitationId)
      onChanged(target, null)
      onMessage({ type: 'success', text: t('users.cabinetInvitation.cancelled') })
    } catch (err) {
      onMessage({
        type: 'error',
        text: displayErrorMessage(getErrorMessage(err), t('users.cabinetInvitation.cancelError')),
      })
    }
  }

  const clearEmailError = () => {
    setView((current) =>
      current?.mode === 'create' && current.emailError !== null ? { ...current, emailError: null } : current,
    )
  }

  const close = () => setOpen(false)

  const handleExited = useCallback(() => {
    setView(null)
    setStudent(null)
  }, [])

  /**
   * Пункты меню строки или карточки. Пусто, если у ученика есть кабинет или
   * договор не активен. `closeMenu` закрывает меню до открытия диалога.
   *
   * «Отправить приглашение ещё раз» показывается только при `can_resend`
   * приглашения: право считает API (`InvitationPolicy::resend`), и пункт,
   * который всё равно упрётся в 403, показывать незачем.
   */
  const getMenuItems = (user: CabinetUser, closeMenu: () => void): ReactElement[] => {
    if (!canManageCabinet(user)) {
      return []
    }

    const target: CabinetInvitationStudent = {
      id: user.id,
      name: getUserDisplayName(user, { prefer: 'full_name' }),
    }
    const invitation = user.cabinet_invitation

    if (!invitation) {
      return [
        <MenuItem
          key="cabinet-create"
          onClick={() => {
            closeMenu()
            openCreate(target)
          }}
        >
          <VpnKeyIcon sx={{ mr: 1 }} fontSize="small" />
          {t('users.cabinetInvitation.create')}
        </MenuItem>,
      ]
    }

    return [
      ...(invitation.can_resend
        ? [
            <MenuItem
              key="cabinet-resend"
              onClick={() => {
                closeMenu()
                void openResend(target, invitation.id)
              }}
            >
              <SendIcon sx={{ mr: 1 }} fontSize="small" />
              {t('users.cabinetInvitation.resend')}
            </MenuItem>,
          ]
        : []),
      <MenuItem
        key="cabinet-cancel"
        onClick={() => {
          closeMenu()
          void cancelInvitation(target, invitation.id, invitation.email)
        }}
        sx={{ color: 'error.main' }}
      >
        <CloseIcon sx={{ mr: 1 }} fontSize="small" />
        {t('users.cabinetInvitation.cancel')}
      </MenuItem>,
    ]
  }

  const dialog = (
    <CabinetInvitationDialog
      open={open}
      view={view}
      onClose={close}
      onExited={handleExited}
      onSubmit={(data) => void submitCreate(data)}
      onEmailChange={clearEmailError}
    />
  )

  return { getMenuItems, openResend, dialog }
}
